import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {buildAdjacencyMap} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';

import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {type TransitionOption} from '../../MergeTransitionModal';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  StaticTemplateFlowchart,
} from '../../processFlowchartConfigEditorConstants';
import {
  isStaticRouterNodeData,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaDomainData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {DeletedNodeContext} from '../../processFlowchartConfigEditorUtils';
import PromoteTransitionModal, {PromoteTransitionContext} from '../../PromoteTransitionModal';
import {getDefaultTransitionSource} from '../../TransitionSourceUtils';
import {type ShowMergeModalFn} from '../../useFlowchartState/useMergeTransitionModal';
import {getNumTransitionSources, isMergingTransition} from './transitionAnalyticsUtils';
import {
  createEmptyTransition,
  getFieldPairKey,
  getOrCreateTransition,
  getPTDVPairKey,
  getTransitionId,
  parseFieldPairKey,
} from './transitionKeyUtils';
import {Transition} from './TransitionTypes';

/**
 * Resolves the source and target PTDV IDs for an edge.
 * When a node has been deleted, its PTDV ID can no longer be found in nodesStaticData.
 * In that case, deletedNodeContext provides the PTDV IDs captured before removal.
 */
export function getPTDVIdsFromEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  deletedNodeContext?: DeletedNodeContext
): {sourcePTDVId: string; targetPTDVId: string} | null {
  const sourceNode = nodes.find((n) => n.id === edge.source);
  const targetNode = nodes.find((n) => n.id === edge.target);

  const sourcePTDVId =
    sourceNode && isStaticTaskSchemaNodeData(sourceNode)
      ? sourceNode.taskDefinitionVersionSpec.clientId
      : deletedNodeContext?.deletedSourceNodePTDVId;

  const targetPTDVId =
    targetNode && isStaticTaskSchemaNodeData(targetNode)
      ? targetNode.taskDefinitionVersionSpec.clientId
      : deletedNodeContext?.deletedTargetNodePTDVId;

  if (sourcePTDVId == null || targetPTDVId == null) {
    return null;
  }

  return {sourcePTDVId, targetPTDVId};
}

export function getFieldPairKeyFromDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  deletedNodeContext?: DeletedNodeContext
): string | null {
  if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }

  const ptdvIds = getPTDVIdsFromEdge(edge, nodes, deletedNodeContext);
  if (!ptdvIds) {
    return null;
  }

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  return getFieldPairKey({
    sourcePTDVId: ptdvIds.sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId: ptdvIds.targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });
}

export interface OrphanedSourcePTDV {
  transitionId: string;
  sourcePTDVId: string;
}

export interface FindOrphanedTransitionIdsAndNodePairsResult {
  orphanedTransitionIds: Array<string>;
  orphanedSourcePTDV: Array<OrphanedSourcePTDV>;
}

function buildNodeToPTDVIdMap(
  nodes: Array<StaticFlowchartNodeData>,
  removedEdge: FlowchartStaticEdgeData,
  deletedNodeContext?: DeletedNodeContext
): Map<string, string> {
  const nodeToPTDVId = new Map<string, string>();
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodeToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }
  // When a router is deleted, the context carries the "effective" PTDV that flows
  // through the router (for pair resolution in getPTDVIdsFromEdge). But the router
  // node itself must not be mapped — it has no PTDV of its own, and the BFS in
  // hasRemainingPathBetweenPTDVs needs to traverse through unmapped nodes.
  if (!deletedNodeContext?.deletedNodeWasRouter) {
    if (deletedNodeContext?.deletedSourceNodePTDVId != null) {
      nodeToPTDVId.set(removedEdge.source, deletedNodeContext.deletedSourceNodePTDVId);
    }
    if (deletedNodeContext?.deletedTargetNodePTDVId != null) {
      nodeToPTDVId.set(removedEdge.target, deletedNodeContext.deletedTargetNodePTDVId);
    }
  }
  return nodeToPTDVId;
}

/**
 * BFS from all nodes matching `sourcePTDVId`, traversing through non-task nodes
 * (routers). Calls `isTarget` on each reached task node and returns true on
 * the first match.
 */
function hasPathFromPTDV(
  adjacencyMap: Record<string, Array<string>>,
  nodeToPTDVId: Map<string, string>,
  sourcePTDVId: string,
  isTarget: (nodeId: string) => boolean
): boolean {
  const startNodeIds: Array<string> = [];
  for (const [nodeId, ptdvId] of nodeToPTDVId.entries()) {
    if (ptdvId === sourcePTDVId) {
      startNodeIds.push(nodeId);
    }
  }

  if (startNodeIds.length === 0) {
    return false;
  }

  const visited = new Set<string>(startNodeIds);
  const queue = [...startNodeIds];
  let i = 0;

  while (i < queue.length) {
    const currentNodeId = queue[i++];

    for (const nextNodeId of adjacencyMap[currentNodeId] ?? []) {
      if (isTarget(nextNodeId)) {
        return true;
      }
      if (visited.has(nextNodeId)) {
        continue;
      }
      visited.add(nextNodeId);

      if (!nodeToPTDVId.has(nextNodeId)) {
        queue.push(nextNodeId);
      }
    }
  }

  return false;
}

function hasRemainingPathBetweenPTDVs(
  adjacencyMap: Record<string, Array<string>>,
  nodeToPTDVId: Map<string, string>,
  sourcePTDVId: string,
  targetPTDVId: string
): boolean {
  return hasPathFromPTDV(
    adjacencyMap,
    nodeToPTDVId,
    sourcePTDVId,
    (nodeId) => nodeToPTDVId.get(nodeId) === targetPTDVId
  );
}

/**
 * Combines two partial orphan-detection results into one.
 *
 * Used when a single removed control-flow edge corresponds to more than one
 * task↔task PTDV pair (e.g. task→router with several downstream tasks): each pair
 * is evaluated separately, then results are folded with this helper.
 *
 * @param a — Accumulated result so far (often from earlier pairs in the loop).
 * @param b — Result from the next pair’s `findOrphanedTransitionsForPTDVPair` call.
 * @returns A `FindOrphanedTransitionIdsAndNodePairsResult`: transition IDs are the
 * union of both inputs (no duplicates); `orphanedSourcePTDV` entries are de-duplicated
 * by `(transitionId, sourcePTDVId)` across `a`, `b`, and duplicates within either input.
 */
function mergeOrphanedTransitionResults(
  a: FindOrphanedTransitionIdsAndNodePairsResult,
  b: FindOrphanedTransitionIdsAndNodePairsResult
): FindOrphanedTransitionIdsAndNodePairsResult {
  const orphanedTransitionIds = [...new Set([...a.orphanedTransitionIds, ...b.orphanedTransitionIds])];
  const getSourceKey = (orphanedSource: OrphanedSourcePTDV): string =>
    `${orphanedSource.transitionId}:${orphanedSource.sourcePTDVId}`;
  const seenSourceKeys = new Set<string>();
  const orphanedSourcePTDVs: Array<OrphanedSourcePTDV> = [];
  for (const orphanedSourcePTDV of [...a.orphanedSourcePTDV, ...b.orphanedSourcePTDV]) {
    const orphanedSourceKey = getSourceKey(orphanedSourcePTDV);
    if (!seenSourceKeys.has(orphanedSourceKey)) {
      seenSourceKeys.add(orphanedSourceKey);
      orphanedSourcePTDVs.push(orphanedSourcePTDV);
    }
  }
  return {orphanedTransitionIds, orphanedSourcePTDV: orphanedSourcePTDVs};
}

function findOrphanedTransitionsForPTDVPair(
  removedEdge: FlowchartStaticEdgeData,
  remainingEdges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  transitions: Record<string, Transition>,
  nodeToPTDVId: Map<string, string>,
  controlFlowAdjacency: Record<string, Array<string>>,
  allEdgesAdjacency: Record<string, Array<string>>,
  sourcePTDVId: string,
  targetPTDVId: string
): FindOrphanedTransitionIdsAndNodePairsResult {
  const EMPTY_RESULT: FindOrphanedTransitionIdsAndNodePairsResult = {
    orphanedTransitionIds: [],
    orphanedSourcePTDV: [],
  };

  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  const transitionIds = ptdvPairToTransitionIdIndex[pairKey];
  if (transitionIds == null || transitionIds.length === 0) {
    return EMPTY_RESULT;
  }

  if (removedEdge.type === ProcessFlowchartEdgeType.CONTROL_FLOW) {
    const hasRemainingDataFlowEdge = remainingEdges.some(
      (edge) =>
        edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
        nodeToPTDVId.get(edge.source) === sourcePTDVId &&
        nodeToPTDVId.get(edge.target) === targetPTDVId
    );

    const result: FindOrphanedTransitionIdsAndNodePairsResult = {
      orphanedTransitionIds: [],
      orphanedSourcePTDV: [],
    };

    for (const transitionId of transitionIds) {
      const transition = transitions[transitionId];
      // Don't mark optional transitions as orphaned
      if (!transition || !transition.isPlanned) {
        continue;
      }

      // When data flow edges remain for this PTDV pair, keep the transition so
      // custom mappings survive — unless the covered-by-merge check below applies.
      const hasDataFlow = hasRemainingDataFlowEdge && transition.sourcePTDVIds.includes(sourcePTDVId);

      const orphanedSourceIds = hasDataFlow
        ? []
        : transition.sourcePTDVIds.filter(
            (sourceId) =>
              !hasRemainingPathBetweenPTDVs(controlFlowAdjacency, nodeToPTDVId, sourceId, targetPTDVId)
          );

      if (orphanedSourceIds.length === transition.sourcePTDVIds.length) {
        result.orphanedTransitionIds.push(transitionId);
      } else if (orphanedSourceIds.length > 0) {
        for (const orphanedSourceId of orphanedSourceIds) {
          result.orphanedSourcePTDV.push({transitionId, sourcePTDVId: orphanedSourceId});
        }
      } else if (
        transition.sourcePTDVIds.length === 1 &&
        isSingleTransitionCoveredByMerge(
          transition.sourcePTDVIds[0],
          targetPTDVId,
          transitionId,
          transitionIds,
          transitions,
          controlFlowAdjacency,
          nodeToPTDVId,
          nodes
        )
      ) {
        result.orphanedTransitionIds.push(transitionId);
      }
    }

    return result;
  }

  // Data flow edge removal: per-source orphan check using all edge types.
  const dataFlowResult: FindOrphanedTransitionIdsAndNodePairsResult = {
    orphanedTransitionIds: [],
    orphanedSourcePTDV: [],
  };

  // For multi-source transitions like {p1, p3} -> p2, only orphan sources that
  // have no remaining edges to the target.
  for (const transitionId of transitionIds) {
    const transition = transitions[transitionId];
    // Don't mark optional transitions as orphaned
    if (!transition || !transition.isPlanned) {
      continue;
    }

    const orphanedSourceIds = transition.sourcePTDVIds.filter(
      (sourceId) => !hasRemainingPathBetweenPTDVs(allEdgesAdjacency, nodeToPTDVId, sourceId, targetPTDVId)
    );

    if (orphanedSourceIds.length === transition.sourcePTDVIds.length) {
      dataFlowResult.orphanedTransitionIds.push(transitionId);
    } else if (orphanedSourceIds.length > 0) {
      for (const orphanedSourceId of orphanedSourceIds) {
        dataFlowResult.orphanedSourcePTDV.push({transitionId, sourcePTDVId: orphanedSourceId});
      }
    }
  }

  return dataFlowResult;
}

/**
 * Returns transitions to delete and sources to remove after an edge was removed.
 *
 * Control flow edge removal: single-source transitions with remaining data flow
 * edges are kept so custom mappings survive, unless the transition has been auto-promoted
 * into a merging transition (in which case it's redundant and orphaned).
 * For transitions without data flow edges, each source is checked for a remaining
 * control flow path to the target. For merging transitions, orphaned sources are
 * removed individually; the transition is deleted only when all sources are orphaned.
 * Single-source transitions with no remaining paths are also orphaned if all their
 * remaining mirrored paths are covered by a merging transition.
 *
 * Data flow edge removal: only deletes the transition when ALL sources have lost
 * their paths (using all edge types). A merging transition configured in advance may
 * have sources without edges drawn yet, so partial disconnection is expected and
 * should not delete the transition.
 *
 * When a node has been deleted, its PTDV ID can no longer be resolved from nodesStaticData.
 * In that case, the caller passes deletedNodeContext with the PTDV IDs captured before the
 * node was removed, so we can still identify the affected transition.
 */
export function findOrphanedTransitionAndSourcePTDVIds(
  removedEdge: FlowchartStaticEdgeData,
  remainingEdges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  transitions: Record<string, Transition>,
  deletedNodeContext?: DeletedNodeContext
): FindOrphanedTransitionIdsAndNodePairsResult {
  const EMPTY_RESULT: FindOrphanedTransitionIdsAndNodePairsResult = {
    orphanedTransitionIds: [],
    orphanedSourcePTDV: [],
  };

  const nodeToPTDVId = buildNodeToPTDVIdMap(nodes, removedEdge, deletedNodeContext);
  const controlFlowAdjacency = buildAdjacencyMap({
    edges: remainingEdges,
    shouldIncludeEdge: (edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW,
  });
  const allEdgesAdjacency = buildAdjacencyMap({
    edges: remainingEdges,
    shouldIncludeEdge: () => true,
  });

  // Direct task↔task (and node-delete) paths: both endpoints are task nodes, or
  // deletedNodeContext supplies PTDVs for removed nodes. Returns null when an
  // endpoint is a router (no PTDV) or a node id is missing — then we resolve
  // task pairs via getTaskSchemaNodePairsForEdge below.
  const ptdvIds = getPTDVIdsFromEdge(removedEdge, nodes, deletedNodeContext);
  if (ptdvIds != null) {
    return findOrphanedTransitionsForPTDVPair(
      removedEdge,
      remainingEdges,
      nodes,
      ptdvPairToTransitionIdIndex,
      transitions,
      nodeToPTDVId,
      controlFlowAdjacency,
      allEdgesAdjacency,
      ptdvIds.sourcePTDVId,
      ptdvIds.targetPTDVId
    );
  }

  // Edges incident to a router have no direct task↔task PTDV pair. Resolve the
  // effective pairs the same way as transition creation (task→router, router→task).
  const edgesBeforeRemoval = [...remainingEdges, removedEdge];
  const taskPairs = getTaskSchemaNodePairsForEdge(removedEdge, nodes, edgesBeforeRemoval);
  if (taskPairs.length === 0) {
    return EMPTY_RESULT;
  }

  const pairKeysSeen = new Set<string>();
  let merged = EMPTY_RESULT;
  for (const {sourceNode, targetNode} of taskPairs) {
    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
    const key = getPTDVPairKey(sourcePTDVId, targetPTDVId);
    if (pairKeysSeen.has(key)) {
      continue;
    }
    pairKeysSeen.add(key);
    merged = mergeOrphanedTransitionResults(
      merged,
      findOrphanedTransitionsForPTDVPair(
        removedEdge,
        remainingEdges,
        nodes,
        ptdvPairToTransitionIdIndex,
        transitions,
        nodeToPTDVId,
        controlFlowAdjacency,
        allEdgesAdjacency,
        sourcePTDVId,
        targetPTDVId
      )
    );
  }

  return merged;
}

/**
 * Find all transitions affected by changes to nodes or edges.
 * Useful for determining which transitions need updates when the flowchart changes.
 */
export function getAffectedTransitions(
  changedNodeIds: Array<string>,
  nodes: Array<StaticFlowchartNodeData>,
  transitions: Record<string, Transition>
): Array<string> {
  if (changedNodeIds.length === 0) {
    return [];
  }

  const changedPTDVIds = new Set<string>();
  for (const nodeId of changedNodeIds) {
    const node = nodes.find((n) => n.id === nodeId);
    if (node && isStaticTaskSchemaNodeData(node)) {
      changedPTDVIds.add(node.taskDefinitionVersionSpec.clientId);
    }
  }

  if (changedPTDVIds.size === 0) {
    return [];
  }

  const affectedTransitionIds: Array<string> = [];
  for (const transition of Object.values(transitions)) {
    const involvesPTDV =
      transition.sourcePTDVIds.some((id) => changedPTDVIds.has(id)) ||
      changedPTDVIds.has(transition.targetPTDVId);

    if (involvesPTDV) {
      affectedTransitionIds.push(transition.id);
    }
  }

  return affectedTransitionIds;
}

export function addCustomMappingForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition,
  customMappingData: CustomMappingData | null = null
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes);
  if (!fieldPairKey) {
    return null;
  }

  return {
    ...transition.customMappingDataByFieldPair,
    [fieldPairKey]: customMappingData,
  };
}

export function addDataFlowFieldPairsToTransition(
  sourcePTDVId: string,
  targetPTDVId: string,
  edges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition
): void {
  for (const edge of edges) {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
      continue;
    }
    const ptdvIds = getPTDVIdsFromEdge(edge, nodes);
    if (ptdvIds?.sourcePTDVId === sourcePTDVId && ptdvIds.targetPTDVId === targetPTDVId) {
      const updatedMappings = addCustomMappingForDataFlowEdge(edge, nodes, transition);
      if (updatedMappings) {
        transition.customMappingDataByFieldPair = updatedMappings;
      }
    }
  }
}

/**
 * Clears the custom mapping for a data flow edge
 */
export function removeCustomMappingForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes);
  if (!fieldPairKey) {
    return null;
  }

  return {
    ...transition.customMappingDataByFieldPair,
    [fieldPairKey]: null,
  };
}

/**
 * Removes a field pair entirely from the transition when a data flow edge is deleted.
 */
export function removeFieldPairForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition,
  deletedNodeContext?: DeletedNodeContext
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes, deletedNodeContext);
  if (!fieldPairKey) {
    return null;
  }

  const {[fieldPairKey]: _, ...remainingMappings} = transition.customMappingDataByFieldPair;
  return remainingMappings;
}

/**
 * Resets custom mappings to null (standard mapping) for any field pair that references
 * one of the changed fields on a given PTDV. The field can appear as either the source
 * or target side of the pair.
 */
export function resetCustomMappingsForChangedFields(
  transitions: Record<string, Transition>,
  ptdvId: string,
  changedFieldClientIds: Set<string>
): void {
  for (const transition of Object.values(transitions)) {
    for (const [fieldPairKey, customMappingData] of Object.entries(transition.customMappingDataByFieldPair)) {
      if (customMappingData == null) {
        continue;
      }
      const parsed = parseFieldPairKey(fieldPairKey);
      if (parsed == null) {
        continue;
      }
      const sourceMatches =
        parsed.sourcePTDVId === ptdvId && changedFieldClientIds.has(parsed.sourceFieldClientId);
      const targetMatches =
        parsed.targetPTDVId === ptdvId && changedFieldClientIds.has(parsed.targetFieldClientId);
      if (sourceMatches || targetMatches) {
        transition.customMappingDataByFieldPair[fieldPairKey] = null;
      }
    }
  }
}

/**
 * Removes a source PTDV from a transition and re-keys it in the transitions record.
 * Since transition IDs are derived from sourcePTDVIds + targetPTDVId, removing a source
 * requires updating the ID and the record key to keep the transition reachable.
 */
export function removeSourcePTDVFromTransition(
  transitions: Record<string, Transition>,
  transitionId: string,
  sourcePTDVId: string
): void {
  const transition = transitions[transitionId];
  if (!transition) {
    return;
  }

  transition.sourcePTDVIds = transition.sourcePTDVIds.filter((id) => id !== sourcePTDVId);
  delete transition.transitionSourcesBySourcePTDVId[sourcePTDVId];
  const prefix = `${sourcePTDVId}:`;
  for (const key of Object.keys(transition.customMappingDataByFieldPair)) {
    if (key.startsWith(prefix)) {
      delete transition.customMappingDataByFieldPair[key];
    }
  }

  const newId = getTransitionId(transition.sourcePTDVIds, transition.targetPTDVId);
  if (newId !== transitionId) {
    transition.id = newId;
    transitions[newId] = transition;
    delete transitions[transitionId];
  }
}

/**
 * Returns true if a merging transition already exists in `transitions` that
 * includes `sourcePTDVId` as one of its sources and targets `targetPTDVId`,
 * AND every source in that transition already has a data-flow path to the
 * specific target node. When the merge is fully connected via data flow to
 * this target node, drawing a control-flow arm is redundant. When it's not
 * (e.g. a different instance of the same PTDV, or a brand-new target node),
 * we still need to create a transition.
 */
function isCoveredByExistingMergingTransition(
  sourcePTDVId: string,
  targetPTDVId: string,
  targetNodeId: string,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>
): boolean {
  const matchingTransitions = Object.values(transitions).filter(
    (t) =>
      t.sourcePTDVIds.length > 1 && t.targetPTDVId === targetPTDVId && t.sourcePTDVIds.includes(sourcePTDVId)
  );
  if (matchingTransitions.length === 0) {
    return false;
  }

  const dataFlowAdjacency = buildAdjacencyMap({
    edges: allEdges,
    shouldIncludeEdge: (edge) => edge.type === ProcessFlowchartEdgeType.DATA_FLOW,
  });
  const nodeToPTDVId = new Map<string, string>();
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodeToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }

  return matchingTransitions.some((t) =>
    t.sourcePTDVIds.every((sourceId) =>
      hasPathToSpecificNode(dataFlowAdjacency, nodeToPTDVId, sourceId, targetNodeId)
    )
  );
}

/**
 * Creates transitions for a newly added control flow edge.
 * Handles three cases:
 * 1. Direct task-to-task edge: Creates a single transition
 * 2. Router-to-task edge: Creates transitions from all upstream task nodes to the target
 * 3. Task-to-router edge: Creates transitions from the source to all downstream task nodes
 *
 * Skips creation when an existing transition already covers the source→target pair
 * (e.g. a merging transition created in the modal that includes the source PTDV).
 *
 * @returns Array of new transitions to be added
 */
export function createTransitionsForNewControlFlowEdge(
  newEdge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>,
  existingTransitions: Record<string, Transition>
): Array<Transition> {
  if (newEdge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return [];
  }
  const newTransitions: Array<Transition> = [];
  const workingTransitions = {...existingTransitions};

  for (const {sourceNode, targetNode} of getTaskSchemaNodePairsForEdge(newEdge, nodes, allEdges)) {
    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;

    if (
      isCoveredByExistingMergingTransition(
        sourcePTDVId,
        targetPTDVId,
        targetNode.id,
        workingTransitions,
        nodes,
        allEdges
      )
    ) {
      continue;
    }

    const {transition, isNew} = getOrCreateTransition({
      sourcePTDVIds: [sourcePTDVId],
      targetPTDVId,
      transitions: workingTransitions,
      isPlanned: true,
    });
    if (isNew) {
      transition.transitionSourcesBySourcePTDVId[sourcePTDVId] = getDefaultTransitionSource(
        sourceNode.id,
        nodes
      );
      addDataFlowFieldPairsToTransition(sourcePTDVId, targetPTDVId, allEdges, nodes, transition);
      newTransitions.push(transition);
    }
  }
  return newTransitions;
}

export interface FlowchartEdgeIndices {
  nodeMap: Map<string, StaticFlowchartNodeData>;
  /** Maps targetNodeId -> [sourceNodeId, ...] for control-flow edges */
  incomingControlFlowMap: Record<string, Array<string>>;
  /** Maps sourceNodeId -> [targetNodeId, ...] for control-flow edges */
  outgoingControlFlowMap: Record<string, Array<string>>;
}

export function buildFlowchartEdgeIndices(
  nodes: Array<StaticFlowchartNodeData>,
  edges: Array<FlowchartStaticEdgeData>
): FlowchartEdgeIndices {
  const nodeMap = new Map<string, StaticFlowchartNodeData>();
  for (const node of nodes) {
    nodeMap.set(node.id, node);
  }
  const incomingControlFlowMap = buildAdjacencyMap({
    edges,
    shouldIncludeEdge: (e) => e.type === ProcessFlowchartEdgeType.CONTROL_FLOW,
    getAdjacencyMapKey: (e) => e.target,
    getAdjacencyMapValue: (e) => e.source,
  });
  const outgoingControlFlowMap = buildAdjacencyMap({
    edges,
    shouldIncludeEdge: (e) => e.type === ProcessFlowchartEdgeType.CONTROL_FLOW,
  });
  return {nodeMap, incomingControlFlowMap, outgoingControlFlowMap};
}

/**
 * Gets the relevant task schema node pairs for a given edge which can be used
 * to identify affected transitions.  Handles the three cases of task-to-task,
 * router-to-task, and task-to-router
 *
 * Pass precomputed `indices` if calling this within a loop for better perf
 */
export function getTaskSchemaNodePairsForEdge(
  edge: Pick<FlowchartStaticEdgeData, 'source' | 'target'>,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>,
  indices?: FlowchartEdgeIndices
): Array<{sourceNode: TaskSchemaDomainData; targetNode: TaskSchemaDomainData}> {
  const resolvedIndices = indices ?? buildFlowchartEdgeIndices(nodes, allEdges);
  const sourceNode = resolvedIndices.nodeMap.get(edge.source);
  const targetNode = resolvedIndices.nodeMap.get(edge.target);

  if (sourceNode == null || targetNode == null) {
    return [];
  }

  const taskSchemaNodePairs: Array<{
    sourceNode: TaskSchemaDomainData;
    targetNode: TaskSchemaDomainData;
  }> = [];

  // Case 1: Direct task-to-task edge
  if (isStaticTaskSchemaNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode)) {
    taskSchemaNodePairs.push({sourceNode, targetNode});
  }

  // Case 2: Router-to-task edge - find upstream task node
  if (isStaticRouterNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode)) {
    const incomingSourceIds = resolvedIndices.incomingControlFlowMap[sourceNode.id] ?? [];
    assert(incomingSourceIds.length <= 1, 'Expected at most one incoming edge');
    const upstreamNode = resolvedIndices.nodeMap.get(incomingSourceIds[0]);
    if (upstreamNode != null && isStaticTaskSchemaNodeData(upstreamNode)) {
      taskSchemaNodePairs.push({sourceNode: upstreamNode, targetNode});
    }
  }

  // Case 3: Task-to-router edge - find all downstream task nodes
  if (isStaticTaskSchemaNodeData(sourceNode) && isStaticRouterNodeData(targetNode)) {
    const outgoingTargetIds = resolvedIndices.outgoingControlFlowMap[targetNode.id] ?? [];
    for (const downstreamId of outgoingTargetIds) {
      const downstreamNode = resolvedIndices.nodeMap.get(downstreamId);
      if (downstreamNode != null && isStaticTaskSchemaNodeData(downstreamNode)) {
        taskSchemaNodePairs.push({sourceNode, targetNode: downstreamNode});
      }
    }
  }

  return taskSchemaNodePairs;
}

/**
 * Returns true if a single transition (sourcePTDVId → targetPTDVId) is still needed
 * on the canvas even after being promoted into a merging transition.
 * For example, A1 -> C1 + B1 -> C1 represents a merging transition {A, B} -> C, but if A2 -> C2 also
 * exists (and B has no path to C2), the single A -> C transition must be kept.
 *
 * When this returns true, the kept single transition will be a subset of the merging
 * transition (same source→target pair), which triggers a subset validation error.
 * The user can then decide which transition to keep and which to delete.
 */
export function singleTransitionStillNeededAfterMergePromotion({
  sourcePTDVId,
  mergingSourcePTDVIds,
  targetPTDVId,
  nodes,
  edges,
}: {
  sourcePTDVId: string;
  mergingSourcePTDVIds: Array<string>;
  targetPTDVId: string;
  nodes: Array<StaticFlowchartNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
}): boolean {
  const controlFlowAdjacency = buildAdjacencyMap({
    edges,
    shouldIncludeEdge: (edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW,
  });
  const nodeToPTDVId = new Map<string, string>();
  const targetNodeIds: Array<string> = [];
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodeToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
      if (node.taskDefinitionVersionSpec.clientId === targetPTDVId) {
        targetNodeIds.push(node.id);
      }
    }
  }

  // For each target node, check whether this source reaches it and
  // whether the full set of merge sources also reaches it. If there's any
  // target node that this source can reach but not all merge sources can,
  // the single transition is still needed independently — the merge doesn't
  // cover that target node so deleting A -> C would lose the A2 -> C2 path.
  for (const targetNodeId of targetNodeIds) {
    const sourcesReachingTarget = mergingSourcePTDVIds.filter((srcPTDV) =>
      hasPathToSpecificNode(controlFlowAdjacency, nodeToPTDVId, srcPTDV, targetNodeId)
    );
    const thisSourceReaches = sourcesReachingTarget.includes(sourcePTDVId);
    if (thisSourceReaches && sourcesReachingTarget.length < mergingSourcePTDVIds.length) {
      return true;
    }
  }

  return false;
}

/**
 * Finds single-source transitions that are fully covered by existing planned
 * merging transitions and can be removed.
 *
 * A single transition (e.g. A → C) is redundant when a merging transition
 * (e.g. {A, B} → C) exists such that every target node instance reachable by A
 * is also reachable by all other merge sources. In that case the single
 * transition carries no independent information.
 *
 * Used after merge promotion (after which we should delete the single transitions that were there before)
 * and after edge addition (new edge may complete a merge).
 */
export function findSingleTransitionsPromotedToMerge({
  mergingTransition,
  transitions,
  nodes,
  edges,
}: {
  mergingTransition: Transition;
  transitions: Record<string, Transition>;
  nodes: Array<StaticFlowchartNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
}): Array<string> {
  const transitionIdsToRemove: Array<string> = [];
  for (const sourcePTDVId of mergingTransition.sourcePTDVIds) {
    const singleTransitionId = getTransitionId([sourcePTDVId], mergingTransition.targetPTDVId);
    if (
      transitions[singleTransitionId] != null &&
      !singleTransitionStillNeededAfterMergePromotion({
        sourcePTDVId,
        mergingSourcePTDVIds: mergingTransition.sourcePTDVIds,
        targetPTDVId: mergingTransition.targetPTDVId,
        nodes,
        edges,
      })
    ) {
      transitionIdsToRemove.push(singleTransitionId);
    }
  }
  return transitionIdsToRemove;
}

/**
 * Given a newly-added edge, finds all single-source transitions that have become
 * redundant because the edge completes a merging transition's source arm.
 */
export function findSingleTransitionsPromotedByNewEdge({
  newEdge,
  transitions,
  nodes,
  edges,
}: {
  newEdge: FlowchartStaticEdgeData;
  transitions: Record<string, Transition>;
  nodes: Array<StaticFlowchartNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
}): Array<string> {
  const idsToRemove: Array<string> = [];
  for (const {sourceNode, targetNode} of getTaskSchemaNodePairsForEdge(newEdge, nodes, edges)) {
    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;

    for (const mergeTransition of Object.values(transitions)) {
      if (
        mergeTransition.sourcePTDVIds.length <= 1 ||
        !mergeTransition.isPlanned ||
        mergeTransition.targetPTDVId !== targetPTDVId ||
        !mergeTransition.sourcePTDVIds.includes(sourcePTDVId)
      ) {
        continue;
      }
      idsToRemove.push(
        ...findSingleTransitionsPromotedToMerge({
          mergingTransition: mergeTransition,
          transitions,
          nodes,
          edges,
        })
      );
    }
  }
  return idsToRemove;
}

/**
 * Creates single-source transitions for merge sources that reach some target node
 * where not all merge sources connect — i.e. the merge is partially disconnected.
 *
 * This is the inverse of findSingleTransitionsPromotedToMerge: after an edge deletion
 * partially disconnects a merge, the remaining arms need their own single
 * transitions so data is preserved.
 *
 * The new transitions inherit field pair mappings and transition source configs
 * from the merge for the relevant source PTDV.
 */
export function createSingleTransitionsForDisconnectedMerge({
  mergingTransition,
  transitions,
  nodes,
  edges,
}: {
  mergingTransition: Transition;
  transitions: Record<string, Transition>;
  nodes: Array<StaticFlowchartNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
}): Array<Transition> {
  const newTransitions: Array<Transition> = [];
  for (const sourcePTDVId of mergingTransition.sourcePTDVIds) {
    const singleTransitionId = getTransitionId([sourcePTDVId], mergingTransition.targetPTDVId);
    if (transitions[singleTransitionId] != null) {
      continue;
    }
    if (
      singleTransitionStillNeededAfterMergePromotion({
        sourcePTDVId,
        mergingSourcePTDVIds: mergingTransition.sourcePTDVIds,
        targetPTDVId: mergingTransition.targetPTDVId,
        nodes,
        edges,
      })
    ) {
      const transition = createEmptyTransition({
        sourcePTDVIds: [sourcePTDVId],
        targetPTDVId: mergingTransition.targetPTDVId,
        isPlanned: true,
      });
      const sourcePrefix = `${sourcePTDVId}:`;
      for (const [key, value] of Object.entries(mergingTransition.customMappingDataByFieldPair)) {
        if (key.startsWith(sourcePrefix)) {
          transition.customMappingDataByFieldPair[key] = value;
        }
      }
      const mergeTransitionSource = mergingTransition.transitionSourcesBySourcePTDVId[sourcePTDVId];
      if (mergeTransitionSource != null) {
        transition.transitionSourcesBySourcePTDVId[sourcePTDVId] = mergeTransitionSource;
      }
      newTransitions.push(transition);
    }
  }
  return newTransitions;
}

/**
 * Given a just-removed edge, creates replacement
 * single-source transitions for any merging transition whose source arm is now
 * partially disconnected from its target.
 */
export function createSingleTransitionsForDisconnectedMergeByRemovedEdge({
  removedEdge,
  transitions,
  nodes,
  edges,
}: {
  removedEdge: FlowchartStaticEdgeData;
  transitions: Record<string, Transition>;
  nodes: Array<StaticFlowchartNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
}): Array<Transition> {
  const newTransitions: Array<Transition> = [];
  for (const taskSchemaNodePair of getTaskSchemaNodePairsForEdge(removedEdge, nodes, edges)) {
    const hasRemainingEdgeForTaskSchemaNodePair = edges.some((edge) =>
      getTaskSchemaNodePairsForEdge(edge, nodes, edges).some(
        (remainingPair) =>
          remainingPair.sourceNode.id === taskSchemaNodePair.sourceNode.id &&
          remainingPair.targetNode.id === taskSchemaNodePair.targetNode.id
      )
    );
    if (hasRemainingEdgeForTaskSchemaNodePair) {
      continue;
    }
    const sourcePTDVId = taskSchemaNodePair.sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = taskSchemaNodePair.targetNode.taskDefinitionVersionSpec.clientId;
    for (const mergeTransition of Object.values(transitions)) {
      if (
        mergeTransition.sourcePTDVIds.length <= 1 ||
        !mergeTransition.isPlanned ||
        mergeTransition.targetPTDVId !== targetPTDVId ||
        !mergeTransition.sourcePTDVIds.includes(sourcePTDVId)
      ) {
        continue;
      }
      newTransitions.push(
        ...createSingleTransitionsForDisconnectedMerge({
          mergingTransition: mergeTransition,
          transitions,
          nodes,
          edges,
        })
      );
    }
  }
  return newTransitions;
}

/**
 * Checks whether a single transition (sourcePTDVId → targetPTDVId) has all of its
 * remaining control-flow paths fully covered by a co-existing merging transition.
 *
 * For each target node instance reachable from sourcePTDVId, checks if a merging
 * transition exists where ALL of its sources also reach that target. If every
 * reachable target is covered this way, the single transition is redundant and
 * can be orphaned.
 */
function isSingleTransitionCoveredByMerge(
  sourcePTDVId: string,
  targetPTDVId: string,
  singleTransitionId: string,
  candidateTransitionIds: Array<string>,
  transitions: Record<string, Transition>,
  controlFlowAdjacency: Record<string, Array<string>>,
  nodeToPTDVId: Map<string, string>,
  nodes: Array<StaticFlowchartNodeData>
): boolean {
  const mergingTransitions = candidateTransitionIds
    .filter((id) => id !== singleTransitionId)
    .map((id) => transitions[id])
    .filter(
      (t): t is Transition =>
        t != null && t.sourcePTDVIds.length > 1 && t.sourcePTDVIds.includes(sourcePTDVId)
    );

  if (mergingTransitions.length === 0) {
    return false;
  }

  const targetNodeIds: Array<string> = [];
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node) && node.taskDefinitionVersionSpec.clientId === targetPTDVId) {
      targetNodeIds.push(node.id);
    }
  }

  for (const targetNodeId of targetNodeIds) {
    if (!hasPathToSpecificNode(controlFlowAdjacency, nodeToPTDVId, sourcePTDVId, targetNodeId)) {
      continue;
    }
    const coveredByAnyMerge = mergingTransitions.some((merge) =>
      merge.sourcePTDVIds.every((srcPTDV) =>
        hasPathToSpecificNode(controlFlowAdjacency, nodeToPTDVId, srcPTDV, targetNodeId)
      )
    );
    if (!coveredByAnyMerge) {
      return false;
    }
  }

  return true;
}

/**
 * Checks whether any node with `sourcePTDVId` can reach the specific `targetNodeId`
 * via control-flow edges (traversing through router/unmapped nodes).
 */
function hasPathToSpecificNode(
  adjacencyMap: Record<string, Array<string>>,
  nodeToPTDVId: Map<string, string>,
  sourcePTDVId: string,
  targetNodeId: string
): boolean {
  return hasPathFromPTDV(adjacencyMap, nodeToPTDVId, sourcePTDVId, (nodeId) => nodeId === targetNodeId);
}

/**
 * Returns whether there is at least one target node (with the transition's target PTDV)
 * where every source PTDV has a control-flow path to that same node.
 */
function allSourcesHaveControlFlowPathsForTransition(
  transition: Transition,
  controlFlowEdges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>
): boolean {
  const controlFlowAdjacency = buildAdjacencyMap({
    edges: controlFlowEdges,
    shouldIncludeEdge: (edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW,
  });
  const nodeToPTDVId = new Map<string, string>();
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodeToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }

  const targetNodeIds = nodes
    .filter(
      (node): node is TaskSchemaDomainData =>
        isStaticTaskSchemaNodeData(node) &&
        node.taskDefinitionVersionSpec.clientId === transition.targetPTDVId
    )
    .map((node) => node.id);

  return targetNodeIds.some((targetNodeId) =>
    transition.sourcePTDVIds.every((sourceId) =>
      hasPathToSpecificNode(controlFlowAdjacency, nodeToPTDVId, sourceId, targetNodeId)
    )
  );
}

/**
 * Gets optional transitions for a newly added control flow edge
 *
 * @returns Array of optional transitions to be promoted
 */
export function getOptionalTransitionsForNewEdge(
  newEdge: Pick<FlowchartStaticEdgeData, 'source' | 'target'>,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>,
  transitions: Record<string, Transition>
): Array<Transition> {
  const optionalTransitions: Array<Transition> = [];
  const seenTransitionIds = new Set<string>();

  // Pre-index optional merging transitions by targetPTDVId so we only
  // scan relevant candidates per node pair instead of all transitions.
  const unplannedMergingTransitionsByTarget = new Map<string, Array<Transition>>();
  for (const transition of Object.values(transitions)) {
    if (!transition.isPlanned && transition.sourcePTDVIds.length >= 2) {
      let bucket = unplannedMergingTransitionsByTarget.get(transition.targetPTDVId);
      if (bucket == null) {
        bucket = [];
        unplannedMergingTransitionsByTarget.set(transition.targetPTDVId, bucket);
      }
      bucket.push(transition);
    }
  }

  for (const {sourceNode, targetNode} of getTaskSchemaNodePairsForEdge(newEdge, nodes, allEdges)) {
    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;

    const singleSourceTransitionId = getTransitionId([sourcePTDVId], targetPTDVId);
    const singleSourceTransition = transitions[singleSourceTransitionId];
    if (singleSourceTransition && !singleSourceTransition.isPlanned) {
      if (!seenTransitionIds.has(singleSourceTransition.id)) {
        optionalTransitions.push(singleSourceTransition);
        seenTransitionIds.add(singleSourceTransition.id);
      }
      continue;
    }

    const candidates = unplannedMergingTransitionsByTarget.get(targetPTDVId);
    if (candidates == null) {
      continue;
    }
    for (const transition of candidates) {
      if (!transition.sourcePTDVIds.includes(sourcePTDVId)) {
        continue;
      }
      if (!allSourcesHaveControlFlowPathsForTransition(transition, allEdges, nodes)) {
        continue;
      }
      if (!seenTransitionIds.has(transition.id)) {
        optionalTransitions.push(transition);
        seenTransitionIds.add(transition.id);
      }
    }
  }
  return optionalTransitions;
}

/**
 * Checks if new edges should be added.
 *
 * If the edges represent new transitions or existing planned transitions,
 * returns true to indicate the edge should be added.
 *
 * If the edges represent optional transitions, shows a modal to ask the user if
 * they want to add the transitions as planned transitions or if they want to
 * cancel adding the edges.
 *
 * When `disallowTransitionEdits` is true, the promotion modal is hard-gated off.
 *
 * @returns A promise that resolves to true if the edge should be added, or false if it should not be added.
 */
export async function shouldAddEdges({
  edges,
  staticFlowchart,
  transitions,
  context,
  flowchartTypeName,
  disallowTransitionEdits = false,
}: {
  edges: Array<FlowchartStaticEdgeData>;
  staticFlowchart: StaticTemplateFlowchart;
  transitions: Record<string, Transition>;
  context?: PromoteTransitionContext;
  flowchartTypeName: string;
  disallowTransitionEdits?: boolean;
}): Promise<boolean> {
  if (disallowTransitionEdits) {
    return true;
  }
  const allEdges: Array<FlowchartStaticEdgeData> = [...staticFlowchart.edgesStaticData, ...edges];
  // Record for deduping
  const optionalTransitions: Record<string, Transition> = {};
  for (const edge of edges) {
    if (edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW) {
      const optionalTransitionsForEdge = getOptionalTransitionsForNewEdge(
        edge,
        staticFlowchart.nodesStaticData,
        allEdges,
        transitions
      );

      for (const transition of optionalTransitionsForEdge) {
        optionalTransitions[getTransitionId(transition.sourcePTDVIds, transition.targetPTDVId)] = transition;
      }
    }
  }

  const getNodeName = (ptdvClientId: string): string => {
    const node = staticFlowchart.nodesStaticData.find(
      (n) => isStaticTaskSchemaNodeData(n) && n.taskDefinitionVersionSpec.clientId === ptdvClientId
    );
    assert(
      node != null && isStaticTaskSchemaNodeData(node),
      'Expected to find task schema node for PTDV client ID'
    );
    const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
    return adapter.getNodeName(
      node.taskDefinitionVersionSpec,
      FlowchartEditorMode.MAIN_CANVAS,
      node.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
    );
  };

  const transitionNames: Array<{sourceNodeName: string; targetNodeName: string}> = [];
  const optionalTransitionsToPromote: Array<Transition> = [];
  for (const optionalTransition of Object.values(optionalTransitions)) {
    const sourceNodeName = optionalTransition.sourcePTDVIds.map((id) => getNodeName(id)).join(', ');
    transitionNames.push({
      sourceNodeName,
      targetNodeName: getNodeName(optionalTransition.targetPTDVId),
    });
    optionalTransitionsToPromote.push(optionalTransition);
  }

  if (transitionNames.length > 0) {
    const result = await runModal(
      PromoteTransitionModal,
      {transitionNames, context},
      {title: 'Add to planned transitions?', size: 'md'}
    );
    if (result == null) {
      for (const transition of optionalTransitionsToPromote) {
        analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.OPTIONAL_TRANSITION_PROMOTION_CANCELLED, {
          actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
          flowchartType: flowchartTypeName,
          transitionId: transition.id,
          isMergingTransition: isMergingTransition(transition),
          numSources: getNumTransitionSources(transition),
        });
      }
    }
    return result != null;
  }
  return true;
}

export interface MergeTransitionCandidate {
  existingTransitions: Array<Transition>;
  newSourcePTDVId: string;
  targetPTDVId: string;
}

/**
 * Finds existing planned transitions targeting the same PTDV as a new control-flow
 * edge. Used to determine whether to offer merge-or-separate.
 */
export function findMergeTransitionCandidates(
  newEdge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>,
  transitions: Record<string, Transition>
): MergeTransitionCandidate | null {
  if (newEdge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return null;
  }

  const taskPairs = getTaskSchemaNodePairsForEdge(newEdge, nodes, allEdges);
  if (taskPairs.length === 0) {
    return null;
  }

  for (const {sourceNode, targetNode} of taskPairs) {
    const newSourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;

    // Self-loops (e.g. loop edges through a router) should not trigger merge prompts
    if (newSourcePTDVId === targetPTDVId) {
      continue;
    }

    const alreadyInTransition = Object.values(transitions).some(
      (t) => t.isPlanned && t.targetPTDVId === targetPTDVId && t.sourcePTDVIds.includes(newSourcePTDVId)
    );
    if (alreadyInTransition) {
      continue;
    }

    // Only consider transitions whose sources actually have edges reaching this
    // specific target node instance, not just any node with the same task schema.
    const sourcePTDVIdsReachingThisNode = new Set<string>();
    for (const edge of allEdges) {
      if (edge.id === newEdge.id) {
        continue;
      }
      for (const pair of getTaskSchemaNodePairsForEdge(edge, nodes, allEdges)) {
        if (pair.targetNode.id === targetNode.id) {
          sourcePTDVIdsReachingThisNode.add(pair.sourceNode.taskDefinitionVersionSpec.clientId);
        }
      }
    }

    const existingTransitions = Object.values(transitions).filter(
      (t) =>
        t.isPlanned &&
        t.targetPTDVId === targetPTDVId &&
        !t.sourcePTDVIds.includes(newSourcePTDVId) &&
        t.sourcePTDVIds.some((id) => sourcePTDVIdsReachingThisNode.has(id))
    );

    if (existingTransitions.length > 0) {
      return {
        existingTransitions,
        newSourcePTDVId,
        targetPTDVId,
      };
    }
  }

  return null;
}

export interface MergeTransitionResult {
  shouldMerge: boolean;
  selectedTransitionId?: string;
}

/**
 * Shows the merge-or-separate modal when a new edge targets a node that already
 * has incoming planned transitions from other sources.
 *
 * Returns null if the user dismisses (close button), or a result indicating
 * whether to merge and which transition to merge into.
 */
export async function promptForMergeTransition({
  candidate,
  nodes,
  flowchartTypeName,
  showMergeModal,
}: {
  candidate: MergeTransitionCandidate;
  nodes: Array<StaticFlowchartNodeData>;
  flowchartTypeName: string;
  showMergeModal: ShowMergeModalFn;
}): Promise<MergeTransitionResult | null> {
  const getNodeDisplayInfo = (
    ptdvClientId: string,
    treatAsOutput: boolean = false
  ): {name: string; icon?: BenchlingIconName} => {
    const node = nodes.find(
      (n) => isStaticTaskSchemaNodeData(n) && n.taskDefinitionVersionSpec.clientId === ptdvClientId
    );
    assert(
      node != null && isStaticTaskSchemaNodeData(node),
      'Expected to find task schema node for PTDV client ID'
    );
    const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
    const isOutput =
      treatAsOutput && node.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
    return {
      name: adapter.getNodeName(node.taskDefinitionVersionSpec, FlowchartEditorMode.MAIN_CANVAS, isOutput),
      icon: adapter.getNodeIcon(node.taskDefinitionVersionSpec, isOutput) ?? undefined,
    };
  };

  const targetDisplay = getNodeDisplayInfo(candidate.targetPTDVId, true);

  const existingTransitions: Array<TransitionOption> = candidate.existingTransitions.map((t) => ({
    transitionId: t.id,
    fromNodes: t.sourcePTDVIds.map((id) => getNodeDisplayInfo(id)),
    toNode: targetDisplay,
  }));

  const result = await showMergeModal({
    existingTransitions,
    newSourceNodeName: getNodeDisplayInfo(candidate.newSourcePTDVId).name,
    targetNodeName: targetDisplay.name,
  });

  if (result == null) {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.MERGE_TRANSITION_MODAL_CANCELLED, {
      actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
      flowchartType: flowchartTypeName,
    });
    return null;
  }

  return result;
}
